/**
 * TransferConfirmModal.js — confirmation step between picking a team
 * member in the TeamMemberPicker and actually firing the transfer.
 *
 * #167. Inserts an optional "note for the new agent" textarea so the
 * outgoing agent can leave hand-off context. The note is persisted as
 * a kind='transfer' row in inbox_lead_notes (via the proxy auto-write
 * hook on /chats/:id/assign) AND surfaced one-shot to the new agent as
 * an ephemeral section divider in their thread.
 *
 * Skip pattern: the modal also has a "Skip note" path (just hit
 * Transfer with an empty textarea) so agents who are mid-rush don't
 * have to type anything. The auto-row still fires with kind='transfer'
 * and an empty body — captures the WHO+WHEN even if the WHAT is missing.
 */

import { html }             from 'htm/preact';
import { useState, useRef } from 'preact/hooks';

const MAX_NOTE = 500;

export function TransferConfirmModal({
  toAgent,                // { email, name }
  fromAgent,              // { email, name } | null  (null = current user releasing self-claim)
  onConfirm,              // (note: string) => Promise<void>
  onCancel,               // () => void
}) {
  const [note, setNote]         = useState('');
  const [submitting, setBusy]   = useState(false);
  const [error, setError]       = useState(null);
  const textareaRef             = useRef(null);

  const trimmed   = note.trim();
  const tooLong   = trimmed.length > MAX_NOTE;
  const canSubmit = !submitting && !tooLong;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(trimmed);
    } catch (e) {
      setError(e?.message || 'Transfer failed');
      setBusy(false);
    }
  };

  // Submit on Cmd/Ctrl+Enter for desktop power-users.
  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const toLabel   = toAgent?.name   || toAgent?.email   || 'team member';
  const fromLabel = fromAgent?.name || fromAgent?.email || null;

  return html`
    <div class="inbox-transfer-modal-backdrop" onClick=${onCancel} role="presentation">
      <div
        class="inbox-transfer-modal-card"
        onClick=${(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="inbox-transfer-modal-title"
      >
        <h2 id="inbox-transfer-modal-title" class="inbox-transfer-modal-title">
          Transfer chat
        </h2>
        <p class="inbox-transfer-modal-sub">
          ${fromLabel
            ? html`Hand off from <strong>${fromLabel}</strong> to <strong>${toLabel}</strong>.`
            : html`Hand off this chat to <strong>${toLabel}</strong>.`}
        </p>

        <label class="inbox-transfer-modal-label" for="inbox-transfer-note">
          Note for ${toAgent?.name?.split(' ')[0] || 'them'} (optional)
        </label>
        <textarea
          id="inbox-transfer-note"
          ref=${textareaRef}
          class="inbox-transfer-modal-textarea"
          placeholder="What's the context? Anything they should pick up on?"
          maxLength=${MAX_NOTE}
          rows="4"
          value=${note}
          onInput=${(e) => setNote(e.target.value)}
          onKeyDown=${handleKeyDown}
          disabled=${submitting}
          autoFocus
        ></textarea>
        <div class="inbox-transfer-modal-meta">
          <span class=${tooLong ? 'inbox-transfer-modal-counter is-over' : 'inbox-transfer-modal-counter'}>
            ${trimmed.length} / ${MAX_NOTE}
          </span>
          <span class="inbox-transfer-modal-hint">Saved to internal notes</span>
        </div>

        ${error && html`
          <div class="inbox-transfer-modal-error" role="alert">${error}</div>
        `}

        <div class="inbox-transfer-modal-actions">
          <button
            type="button"
            class="inbox-btn inbox-btn-ghost"
            onClick=${onCancel}
            disabled=${submitting}
          >Cancel</button>
          <button
            type="button"
            class="inbox-btn inbox-btn-primary"
            onClick=${handleSubmit}
            disabled=${!canSubmit}
          >${submitting ? 'Transferring…' : 'Transfer'}</button>
        </div>
      </div>
    </div>
  `;
}
